import * as yup from "yup";

import { DEFAULT_KYC_WINDOW, HOLDER_TYPE, RECORD_STATUS } from "./constants";
import { CURRENCY, DEBIT_NOTE_FREQUENCY, PAYMENT_MODEL } from "@constants/panel/common";

// ─── Ops account ──────────────────────────────────────────────────────────────
// POST /ops/v1 — the only account that can be made without an existing session,
// and the only ops call this console makes.
export const opsAccountSchema = yup.object({
    name    : yup.string().trim().required("Name is required").default(""),
    email   : yup.string().trim().email("Enter a valid email").required("Email is required").default(""),
    password: yup.string().min(8, "Use at least 8 characters").required("Password is required").default("")
});

export const opsAccountDefaults = opsAccountSchema.getDefault();

// ─── Brand ────────────────────────────────────────────────────────────────────
// POST /onboarding/account/v1. `ext_id` is the brand's id on the tracking
// platform and is what holder create later files its logins against.
export const brandSchema = yup.object({
    name                : yup.string().trim().required("Brand name is required").default(""),
    ext_id              : yup.string().trim().required("External id is required").default(""),
    status              : yup.string().trim().required().default(RECORD_STATUS.ACTIVE),
    payment_model       : yup.string().trim().required("Payment model is required").default(PAYMENT_MODEL.MANAGED_PAYOUT),
    debit_note_frequency: yup.string().trim().required("Debit note frequency is required").default(DEBIT_NOTE_FREQUENCY.MONTHLY),
    platform_name       : yup.string().trim().required("Platform is required").default("trackier"),
    kyc_window          : yup
        .number()
        .typeError("KYC window must be a number")
        .min(1, "KYC window must be at least 1 day")
        .required("KYC window is required")
        .default(DEFAULT_KYC_WINDOW),
    notes: yup.string().trim().default("")
});

export const brandDefaults = brandSchema.getDefault();

// ─── Brand logins ─────────────────────────────────────────────────────────────
// POST /onboarding/holder/v1 takes an array, and a brand's two logins are made
// together — so one form holds both sides and each side can be switched off if
// only one is wanted. The fields are flat (admin_*, affiliate_*) rather than
// nested so `when()` can see the checkbox that governs them.
// Every rule for a side sits inside its `when` branch — a switched-off side is
// left holding empty strings, and yup's `min` fails on "" (it only skips
// null/undefined), so a length rule outside the branch would block the submit
// over fields the batch is not even going to carry. Passwords are not trimmed:
// leading or trailing space is part of the password the API is given.
const whenEnabled = (flag, tighten) => yup.string().when(flag, { is: true, then: tighten });

export const brandLoginsSchema = yup.object({
    account_id: yup.string().trim().required("Pick a brand").default(""),

    create_admin  : yup.boolean().default(true),
    admin_name    : whenEnabled("create_admin", (s) => s.trim().required("Name is required")).default(""),
    admin_ext_id  : whenEnabled("create_admin", (s) => s.trim().required("External id is required")).default(""),
    admin_email   : whenEnabled("create_admin", (s) => s.trim().email("Enter a valid email").required("Email is required")).default(""),
    admin_password: whenEnabled("create_admin", (s) => s.min(6, "Use at least 6 characters").required("Password is required")).default(""),

    create_affiliate  : yup.boolean().default(true),
    affiliate_name    : whenEnabled("create_affiliate", (s) => s.trim().required("Name is required")).default(""),
    affiliate_ext_id  : whenEnabled("create_affiliate", (s) => s.trim().required("External id is required")).default(""),
    affiliate_email   : whenEnabled("create_affiliate", (s) => s.trim().email("Enter a valid email").required("Email is required")).default(""),
    affiliate_password: whenEnabled("create_affiliate", (s) => s.min(6, "Use at least 6 characters").required("Password is required")).default("")
});

export const brandLoginsDefaults = brandLoginsSchema.getDefault();

// The brand is the other half of every holder body: `api_key` authenticates the
// call and `account_ext_id` says which brand the login belongs to. Neither is
// typed in — both are read off the row the brand list handed back.
export const toHolderPayload = (values, brand) => {
    const sides = [
        {
            enabled : values.create_admin,
            type    : HOLDER_TYPE.ADMIN,
            name    : values.admin_name,
            ext_id  : values.admin_ext_id,
            email   : values.admin_email,
            password: values.admin_password
        },
        {
            enabled : values.create_affiliate,
            type    : HOLDER_TYPE.AFFILIATE,
            name    : values.affiliate_name,
            ext_id  : values.affiliate_ext_id,
            email   : values.affiliate_email,
            password: values.affiliate_password
        }
    ];

    return sides.filter((side) => side.enabled).map((side) => ({
        api_key       : brand.api_key,
        email         : side.email,
        account_ext_id: brand.ext_id,
        holder_ext_id : side.ext_id,
        holder_name   : side.name,
        holder_type   : side.type,
        password      : side.password
    }));
};

// ─── Publisher invoice ────────────────────────────────────────────────────────
// The only filters the brand-scoped affiliate list documents — the brand
// itself is in the path, not the query. Blank fields are left out of the query
// rather than sent as "", so the endpoint never sees an empty filter.
export const affiliateFiltersSchema = yup.object({
    search       : yup.string().trim().default(""),
    kyc_status   : yup.string().trim().default(""),
    payout_status: yup.string().trim().default("")
});

export const defaultAffiliateFilters = affiliateFiltersSchema.getDefault();

export const toAffiliateFilterParams = async (filters) => {
    const validated = await affiliateFiltersSchema.validate(filters, { stripUnknown: true });
    return Object.entries(validated).reduce((params, [key, value]) => {
        if (value) params[key] = value;
        return params;
    }, {});
};

// POST /invoices/v1?api_key=… . The publisher is not a field on this form — it
// is the row the invoice was raised from, and `holder_id` is read off that row.
// The four money fields are independent: the sample's amount (2560) is not its
// subtotal less fee plus tax (6000), so nothing here is derived.
const money = (label) => yup
    .number()
    .typeError(`${label} must be a number`)
    .min(0, `${label} cannot be negative`)
    .required(`${label} is required`);

export const invoiceSchema = yup.object({
    payout_period : yup.string().trim().required("Payout period is required").default(""),
    invoice_number: yup.string().trim().required("Invoice number is required").default(""),
    ext_invoice_id: yup.string().trim().required("External invoice id is required").default(""),
    amount        : money("Amount").moreThan(0, "Amount must be more than 0").default(0),
    currency      : yup.string().trim().required("Currency is required").default(CURRENCY),
    subtotal      : money("Subtotal").default(0),
    fee           : money("Fee").default(0),
    tax           : money("Tax").default(0),
    issue_date    : yup.string().trim().required("Issue date is required").default(""),
    due_date      : yup
        .string()
        .trim()
        .required("Due date is required")
        // Plain ISO day strings, so a lexical compare is a date compare.
        .test("after-issue", "Due date cannot be before the issue date", (value, ctx) => !value || !ctx.parent.issue_date || value >= ctx.parent.issue_date)
        .default(""),
    description: yup.string().trim().default("")
});

export const toInvoicePayload = (values, holderId) => ({
    holder_id     : holderId,
    payout_period : values.payout_period,
    invoice_number: values.invoice_number,
    ext_invoice_id: values.ext_invoice_id,
    amount        : Number(values.amount),
    currency      : values.currency,
    subtotal      : Number(values.subtotal),
    fee           : Number(values.fee),
    tax           : Number(values.tax),
    issue_date    : values.issue_date,
    due_date      : values.due_date,
    description   : values.description
});
